import { AttributeTypes, GoogleAddressComponentTypes } from "@iddataweb/idw-common-ui/enums";
import { AttributeStatusTypes } from "@enums/attribute-status-types";
import { SMSPINDeliveryPreference } from "@constants/form-constants";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { OOBStatusTypes } from "@enums/oob-status-types";

export const getFullNameValue = (firstNameValue: string, middleNameValue: string, lastNameValue: string) => {
	return `${firstNameValue ? `${firstNameValue} ` : ''}${middleNameValue ? `${middleNameValue} ` : ''
		}${lastNameValue ? `${lastNameValue}` : ''}`;
};

export const getSSNValue = (areaNumber: string, groupNumber: string, serialNumber: string) => {
	return `${areaNumber ? `${areaNumber}` : ''}${groupNumber ? `${groupNumber}` : ''}${serialNumber ? `${serialNumber}` : ''}`;
};

export const getIndividualSSNComponents = (ssn9: string) => {
	if (ssn9 && ssn9.length === 9) {
		const areaNumber = ssn9.substring(0, 3);
		const groupNumber = ssn9.substring(3, 5);
		const serialNumber = ssn9.substring(5);
		return {
			areaNumber,
			groupNumber,
			serialNumber,
			ssn9
		};
	} else {
		return {
			areaNumber: "",
			groupNumber: "",
			serialNumber: "",
			ssn9: ""
		};
	}
};

export const getAttributePlaceHolderValue = (attribute: AttributeFormState) => {
	const prefillValue = attribute.prefillValueMap || {};
	const parameter = attribute.type.includes('CustomAttribute')
		? attribute.templateName
		: attribute.type;
	switch (parameter) {
		case AttributeTypes.FULL_NAME: {
			const fullNameValue = getFullNameValue(
				prefillValue.fname,
				prefillValue.mname,
				prefillValue.lname
			);
			return fullNameValue;
		}
		case AttributeTypes.COMPANY_NAME:
			return prefillValue.alphanumeric;
		case AttributeTypes.INTERNATIONAL_TELEPHONE:
			return `${prefillValue.dialCode || ''} ${prefillValue.telephone || ''}`;
		case AttributeTypes.PIN_DELIVERY_PREFERENCE:
			return prefillValue.alphanumeric;
		case AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE:
			return prefillValue.alphanumeric;
		case AttributeTypes.EMAIL:
			return prefillValue.email;
		case AttributeTypes.DOB:
			return prefillValue.dateofbirth;
		case AttributeTypes.NATIONAL_ID:
			return prefillValue.nationalid;
		case AttributeTypes.DATE:
			return prefillValue.date;
		case AttributeTypes.GENDER:
			return prefillValue.gender;
		case AttributeTypes.COUNTRY:
			return prefillValue.country;
		case AttributeTypes.LICENSE_OR_ID_CARD:
			return prefillValue.licenseOrIDCard;
		case AttributeTypes.LICENSE_OR_PASSPORT:
		case AttributeTypes.INTERNATIONAL_LICENSE_OR_PASSPORT:
		case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
			return prefillValue.licenseOrPassport;
		case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
			return prefillValue.licensePassportOrIDCard;
		case AttributeTypes.PASSPORT_OR_ID_CARD:
			return prefillValue.passportOrIDCard;
		case AttributeTypes.DRIVERS_LICENSE_STATE:
			return prefillValue.driversLicenseState;
		case AttributeTypes.ALPHANUMERIC:
		case AttributeTypes.ALPHANUMERIC_OTHER:
			return prefillValue.alphanumeric;
		case AttributeTypes.NUMERIC:
			return prefillValue.numeric;
		case AttributeTypes.BIO_GOV_OR_KBA:
			return prefillValue.bioGovOrKbaSelection;
		case AttributeTypes.NATIONALITY:
			return prefillValue.nationality;
		default:
			return '';
	}
};

const getAttributeInitialValue = (attribute: AttributeFormState, previousAttributeData: AttributeFormState) => {
	const prefillValue = attribute.prefillValueMap || {};
	if (attribute.isPlaceholder || Object.keys(prefillValue).length > 0) {
		return getAttributePlaceHolderValue(attribute);
	} else if (attribute.hasPreSelectedSinglePickerValue && previousAttributeData) {
		return previousAttributeData.masked;
	} else if (attribute.hasPickerDefaultValue) {
		return attribute.pickerDefaultAttribute.masked;
	} else {
		return '';
	}
};

export const getAttributeFormInitialValue = (attribute: AttributeFormState, previousAttributeData: AttributeFormState) => {
	const prefillValue = attribute.prefillValueMap || {};
	const parameter = attribute.type.includes('CustomAttribute')
		? attribute.templateName
		: attribute.type;
	switch (parameter) {
		case AttributeTypes.PIN_DELIVERY_PREFERENCE:
			return prefillValue && prefillValue.pindeliverypreference
				? prefillValue.pindeliverypreference
				: SMSPINDeliveryPreference;
		case AttributeTypes.EMAIL_OR_PHONE_PIN_PREFERENCE:
			return prefillValue && prefillValue.emailorphonepinpreference
				? prefillValue.emailorphonepinpreference
				: SMSPINDeliveryPreference;
		case AttributeTypes.EMAIL:
			return prefillValue && prefillValue.email
				? prefillValue.email
				: getAttributeInitialValue(attribute, previousAttributeData);
		case AttributeTypes.FULL_NAME:
		case AttributeTypes.COMPANY_NAME:
		case AttributeTypes.INTERNATIONAL_TELEPHONE:
		case AttributeTypes.INTERNATIONAL_ADDRESS:
		case AttributeTypes.DOB:
		case AttributeTypes.NATIONAL_ID:
		case AttributeTypes.DATE:
		case AttributeTypes.GENDER:
		case AttributeTypes.COUNTRY:
		case AttributeTypes.DRIVERS_LICENSE_NUMBER:
		case AttributeTypes.PASSPORT_NUMBER:
		case AttributeTypes.SSN9:
		case AttributeTypes.SSN4:
		case AttributeTypes.LICENSE_OR_ID_CARD:
		case AttributeTypes.LICENSE_OR_PASSPORT:
		case AttributeTypes.LICENSE_OR_PASSPORT_OR_BROWSE:
		case AttributeTypes.LICENSE_PASSPORT_OR_ID_CARD:
		case AttributeTypes.PASSPORT_OR_ID_CARD:
		case AttributeTypes.AADHAR_PAN_OR_PASSPORT:
		case AttributeTypes.DRIVERS_LICENSE_STATE:
		case AttributeTypes.PHYSICIAN_LICENSE_NUMBER:
		case AttributeTypes.PAN_NUMBER:
		case AttributeTypes.AADHAR_CARD_NUMBER:
		case AttributeTypes.ALPHANUMERIC:
		case AttributeTypes.ALPHANUMERIC_OTHER:
		case AttributeTypes.NUMERIC:
		case AttributeTypes.BIO_GOV_OR_KBA:
		case AttributeTypes.NATIONALITY:
			return getAttributeInitialValue(attribute, previousAttributeData);
		default:
			return '';
	}
};

const getAttributeDefaultValue = (attribute: AttributeFormState) => {
	const value = {
		id: attribute.id,
		attributeType: attribute.type,
		status: attribute.status,
		data: {} as { [key: string]: string | number }
	};

	return value;
};

export const getAttributeSubmitData = (attribute: AttributeFormState) => {
	let data: { [key: string]: number | string } = {};
	switch (attribute.status) {
		case AttributeStatusTypes.NEW:
		case AttributeStatusTypes.FAIL:
		case AttributeStatusTypes.PASS:
			data = {
				id: attribute.selectedAttributeId
			};
			break;
		case AttributeStatusTypes.PICKER: {
			let attrID;
			if (attribute.inputData !== undefined) {
				attrID = attribute.inputData.id;
				if (attrID === undefined) {
					attrID = attribute.pickerData.id;
				}
			} else {
				attrID = attribute.pickerDefaultValue;
			}

			data = {
				id: attrID
			};
			break;
		}
		case AttributeStatusTypes.PIN:
			data = {
				id: attribute.selectedAttributeId,
				pincode: attribute.inputData.pincode
			};

			break;
		case AttributeStatusTypes.PLACEHOLDER:
			data = {
				id: -1
			};
			break;
		default:
			break;
	}

	return data;
};

const checkIfAllOtherFieldsEmpty = (
	attributeInputData: { [key: string]: string },
	fieldNameToRemove: string
) => {
	const restOfFields: { [key: string]: string } = {
		...attributeInputData,
		[fieldNameToRemove]: null
	};
	return !Object.values(restOfFields).some(value => !!value);
};

export const getAttributeSubmitValue = (attribute: AttributeFormState) => {
	const defaultValue = getAttributeDefaultValue(attribute);
	const basicData = getAttributeSubmitData(attribute);
	const attributeInputData = { ...attribute.inputData };

	//international address that has a country field
	if (
		attribute.type === AttributeTypes.INTERNATIONAL_ADDRESS &&
		attribute.inputData[GoogleAddressComponentTypes.COUNTRY] &&
		checkIfAllOtherFieldsEmpty(attribute.inputData, GoogleAddressComponentTypes.COUNTRY)
	) {
		//if no other fields are populated, clear country
		attributeInputData[GoogleAddressComponentTypes.COUNTRY] = null;
	} else if (
		attribute.type === AttributeTypes.INTERNATIONAL_TELEPHONE &&
		attribute.inputData["dialCode"] &&
		checkIfAllOtherFieldsEmpty(attribute.inputData, "dialCode")
	) {
		//if no other fields are populated, clear dialcode
		attributeInputData["dialCode"] = null;
	}

	if (attribute.status === AttributeStatusTypes.NEW && attribute.oobStatus !== OOBStatusTypes.COMPLETE) {
		defaultValue.data = { ...attributeInputData, ...basicData };
	} else {
		defaultValue.data = basicData;
	}

	Object.keys(defaultValue.data).forEach((key) => {
		if (key !== 'id' && !defaultValue.data[key]) {
			defaultValue.data[key] = null;
		}
	});

	return defaultValue;
};